/**
 * Lógica pura de reabastecimiento (CC-6: NUNCA usar macros lingui aquí).
 *
 * Una regla (reorder_rule) define mín/máx por producto×almacén. Cuando el
 * on-hand cae por debajo del mínimo, se sugiere reabastecer hasta el máximo
 * (o hasta el mínimo si no hay máximo definido). Determinista, sin AI.
 *
 * Cantidades en unidades menores (enteros, factor 1000), igual que F1.
 */

export interface ReorderRuleLike {
  id: string;
  product_id: string | null;
  warehouse_id: string | null;
  min_qty: number | null;
  max_qty: number | null;
}

export interface ReplenishmentSuggestion {
  ruleId: string;
  productId: string;
  warehouseId: string | null;
  onHand: number;
  minQty: number;
  maxQty: number;
  /** Cantidad a reabastecer (> 0). target − onHand. */
  suggestedQty: number;
}

/**
 * Calcula la cantidad sugerida para una regla dado el on-hand actual. Devuelve 0
 * si el stock está en o por encima del mínimo (no hay que reabastecer). El
 * objetivo es el máximo; si el máximo es <= mínimo, se reabastece hasta el mínimo.
 */
export function computeSuggestedQty(onHand: number, minQty: number, maxQty: number): number {
  if (onHand >= minQty) {
    return 0;
  }
  const target = maxQty > minQty ? maxQty : minQty;
  const suggested = target - onHand;
  return Math.max(suggested, 0);
}

/**
 * Genera la lista de sugerencias de reabastecimiento a partir de las reglas y
 * un mapa de on-hand por (producto, almacén). El on-hand se resuelve por almacén
 * cuando la regla fija warehouse_id; si la regla es global (sin almacén), usa el
 * on-hand total del producto.
 *
 * @param onHandByKey  clave `${productId} ${warehouseId ?? ""}` → on-hand.
 * @param onHandByProduct  productId → on-hand total (fallback para reglas globales).
 */
export function computeReplenishmentSuggestions(
  rules: ReorderRuleLike[],
  onHandByKey: Map<string, number>,
  onHandByProduct: Map<string, number>,
): ReplenishmentSuggestion[] {
  const suggestions: ReplenishmentSuggestion[] = [];
  for (const rule of rules) {
    const productId = rule.product_id;
    if (!productId) {
      continue;
    }
    const minQty = rule.min_qty ?? 0;
    const maxQty = rule.max_qty ?? 0;
    const onHand = rule.warehouse_id
      ? (onHandByKey.get(`${productId} ${rule.warehouse_id}`) ?? 0)
      : (onHandByProduct.get(productId) ?? 0);
    const suggestedQty = computeSuggestedQty(onHand, minQty, maxQty);
    if (suggestedQty > 0) {
      suggestions.push({
        ruleId: rule.id,
        productId,
        warehouseId: rule.warehouse_id,
        onHand,
        minQty,
        maxQty,
        suggestedQty,
      });
    }
  }
  return suggestions;
}
